import { ProjectElementType } from '@deltek/specification-client/core/Models/projectElement'
import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { useCallback } from 'react'
import { v4 as newGuid } from 'uuid'
import { useElementItemShallowSelector } from '../ElementItemStore'
import {
    useShallowSelector,
    useTextContentElementToAdd
} from '../Workspace2Store'
import {
    ArticleInputBuilder,
    ObjectBuilder,
    ParagraphInputBuilder,
} from '../factories'
import { useNewContentCreation } from '../features/scrollToNewContent'
import { useContentMutationService, useTransactionManager } from '../services'
import type { ContentItemViewNodesType, DataNode } from '../types'
import { replaceNodeNotesByKey } from '../utils'

/**
 * Custom hook to handle actions related to Content Item View Nodes
 * @returns Actions related to Content Item View Nodes
 */
export const useContentItemViewNode = () => {
    const nodeToAdd = useTextContentElementToAdd()
    const { markAsNewlyCreated } = useNewContentCreation()

    const { executeWithOptimistic } = useTransactionManager()
    const { mutateNoteAsync } = useContentMutationService()

    const {
        contentItemViewNodes,
        selectedProjectElementNodeId,
        familyAddedProductTypes,
        previousContentItemViewNodes,
        setContentItemViewNodes,
        setTextContentElementToAdd,
        setPreviousContentItemViewNodes,
    } = useShallowSelector((s) => ({
        contentItemViewNodes: s.contentItemViewNodes,
        selectedProjectElementNodeId: s.selectedProjectElementNodeId,
        familyAddedProductTypes: s.familyAddedProductTypes,
        previousContentItemViewNodes: s.previousContentItemViewNodes,
        setContentItemViewNodes: s.actions.setContentItemViewNodes,
        setTextContentElementToAdd: s.actions.setTextContentElementToAdd,
        setPreviousContentItemViewNodes:
            s.actions.setPreviousContentItemViewNodes,
    }))
    /** the element selected on Element Tree */
    const { elementType } = useElementItemShallowSelector(
        (s) => s.elementItemValue
    )
    /**
     * Finds a Content Item Node by its id.
     */
    const findContentItemNode = useCallback(
        (id: string) => contentItemViewNodes.find((node) => node.id === id),
        [contentItemViewNodes]
    )

    /**
     * Inserts a new node after a sibling node
     */
    const insertNodeAfterSibling = useCallback(
        (node: Partial<ContentItemViewNodesType>, siblingId?: string) => {
            // Clear any related children nodes to prevent mapping issues
            node.dtvItemId = undefined

            // Snapshot current tree structure before mutation
            setPreviousContentItemViewNodes(contentItemViewNodes)

            // Sort nodes by order and find sibling's position
            const sortedNodes = [...contentItemViewNodes].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            const siblingIndex = sortedNodes.findIndex(n => n.id === siblingId)

            if (siblingIndex !== -1) {
                // Insert at the found position
                sortedNodes.splice(siblingIndex, 0, node)
                setContentItemViewNodes(sortedNodes)
            }
        },
        [contentItemViewNodes, setPreviousContentItemViewNodes, setContentItemViewNodes]
    )

    /**
     * Sets the currently being added content and registers it for scroll-into-view action
     */
    const setAddingNode = useCallback(
        (node: Partial<ContentItemViewNodesType>) => {
            // Need the text content element as it's used by the content renderer
            const textContentElement = node.elements?.[0]

            if (textContentElement) {
                // Update the store which node is currently being added
                setTextContentElementToAdd({
                    ...structuredClone(textContentElement),
                    type: node.textType,
                })

                // mark as newly created and trigger autofocus scroll
                markAsNewlyCreated(textContentElement.id)
            }
        },
        [setTextContentElementToAdd, markAsNewlyCreated]
    )

    /**
     * Creates a Text content item node.
     * @param {Partial<TextContentElement>} overrides - Optional overrides for the content item properties
     * @returns Text content item node with a text element
     */
    const createTextContentElement = useCallback(
        (overrides?: Partial<TextContentElement>) => {
            return new ObjectBuilder<TextContentElement>()
                .set('id', overrides?.id || newGuid())
                .set('isOptionItem', overrides?.isOptionItem)
                .set('imperialUnits', overrides?.imperialUnits)
                .set('metricUnits', overrides?.metricUnits)
                .set('optionItemState', overrides?.optionItemState || null)
                .set('fieldId', overrides?.fieldId || '')
                .set('text', overrides?.text || '')
                .set('type', 'Text') // should always be 'Text' type, cannot override
                .set('placeholderText', overrides?.placeholderText || '')
                .set('isPremiumPlus', overrides?.isPremiumPlus || false)
                .set('isBestPractice', overrides?.isBestPractice || false)
                .set(
                    'isGlobalBestPractice',
                    overrides?.isGlobalBestPractice || false
                )
                .set('changeType', overrides?.changeType || null)
                .set(
                    'authoredUserId',
                    overrides?.authoredUserId || 'anonymous1'
                )
                .set('elements', overrides?.elements || [])
                .build()
        },
        []
    )

    /**
     * Creates a content item node with positioning metadata for fast path insertion.
     * The _insertAfterId field is CRITICAL for O(1) tree insertion optimization.
     * @param text - Text to be displayed in the content item
     * @param siblingNode - Sibling content item to insert this node after
     * @returns Content item node with a text element
     */
    const createContentItemViewNode = useCallback(
        (
            text: string,
            siblingNode?: Partial<ContentItemViewNodesType>
        ): Partial<ContentItemViewNodesType> => {
            return new ObjectBuilder<ContentItemViewNodesType>(siblingNode)
                .set('id', newGuid())
                .set('order', (siblingNode?.order || 0) + 1)
                .set('_insertAfterId', siblingNode?.id) // Fast path positioning metadata
                .set('paragraphOrder', (siblingNode?.paragraphOrder || 0) + 1)
                .set('comments', [])
                .set('parentComments', [])
                .set('isSharedProperty', false)
                .set('isProductType', false)
                .set('isForProductType', false)
                .set('isProductGroup', false)
                .set('isBestPractice', false)
                .set('isGlobalBestPractice', false)
                .set('isHidden', false)
                .set('isUserAdded', true)
                .set('isManufacturers', false)
                .set('isBOD', false)
                .set('elements', [
                    createTextContentElement({
                        text,
                        changeType: 'ADDED',
                        elements:
                            siblingNode?.textType === 'Article'
                                ? [
                                      {
                                          id: newGuid(),
                                          text:
                                              'Insert your paragraph, or, paste content.',
                                      },
                                  ]
                                : [],
                    }),
                ])
                .build()
        },
        [createTextContentElement]
    )

    /**
     * Updates the text content of a node in the content item view nodes.
     * @param nodeId - The ID of the node to update.
     * @param text - The new text content to set.
     * @param nodes - The array of content item view nodes.
     * @returns The updated array of content item view nodes.
     */
    const updateText = useCallback(
        (
            nodeId: string = '',
            text: string,
            nodes: Partial<ContentItemViewNodesType>[]
        ) => {
            const updated = structuredClone(nodes)

            // find and update the target node's element
            for (const node of updated) {
                if (!node.elements) continue

                const elementIndex = node.elements.findIndex(
                    (el: Partial<TextContentElement>) => el.id === nodeId
                )

                if (elementIndex === -1) continue

                node.elements[elementIndex] = {
                    ...node.elements[elementIndex],
                    text, // set the new text value
                }

                break
            }

            return updated
        },
        []
    )

    /**
     * Handles optimistic update for adding a new content item.
     * @param unsavedData - The text value of the new content item.
     * @param isSuccess - A flag indicating whether the optimistic update was successful.
     */
    const handleOptimisticAddAction = useCallback(
        (unsavedData: string, isSuccess: boolean = false) => {
            if (isSuccess) {
                const updatedNodes = updateText(
                    nodeToAdd?.id,
                    unsavedData,
                    contentItemViewNodes
                )
                setContentItemViewNodes(updatedNodes)
                setTextContentElementToAdd(null)
            } else {
                setContentItemViewNodes(previousContentItemViewNodes || [])
                setTextContentElementToAdd(null)
            }
        },
        [
            nodeToAdd,
            updateText,
            contentItemViewNodes,
            previousContentItemViewNodes,
        ]
    )

    /**
     * Execute the mutation to add new article to the project with optimistic update.
     * @param unsavedData - The text value of new article.
     */
    const addNewArticle = useCallback(
        (unsavedData: string) => {
            if (!nodeToAdd || !nodeToAdd.id) return

            const articleInput = new ArticleInputBuilder(
                unsavedData,
                nodeToAdd.id,
                contentItemViewNodes
            )
            const variables = articleInput.build()

            return variables
        },
        [nodeToAdd, contentItemViewNodes]
    )

    /**
     * Execute the mutation to add new paragraph to the project with optimistic update.
     * @param unsavedData - The text value of new paragraph.
     */
    const addNewParagraph = useCallback(
        (unsavedData: string) => {
            if (!nodeToAdd || !nodeToAdd.id) return

            const paragraphInput = new ParagraphInputBuilder(
                unsavedData,
                nodeToAdd.id,
                contentItemViewNodes,
                familyAddedProductTypes,
                selectedProjectElementNodeId,
                elementType as ProjectElementType
            )
            const variables = paragraphInput.build()

            return variables
        },
        [nodeToAdd, contentItemViewNodes]
    )

    /**
     * Handles the deletion of a specifier note.
     * @param node - The node containing the specifier notes.
     * @param noteIdx - The index of the note to be deleted.
     */
    const deleteSpecifierNote = useCallback(
        (node: DataNode, noteIdx: number, isArticleNoteSpecifier?: boolean) => {
            const notes =
                (isArticleNoteSpecifier
                    ? node.parentComments
                    : node.specifierNotes) || []

            if (!notes?.[noteIdx]) {
                console.warn('deleteSpecifierNote: note not found', {
                    notes,
                    noteIdx,
                })
                return
            }

            const updatedNotes = [...notes]
            // remove the note from the array
            updatedNotes.splice(noteIdx, 1)

            const optimisticTreeViewNodes = replaceNodeNotesByKey(
                contentItemViewNodes,
                node.key,
                updatedNotes,
                isArticleNoteSpecifier
            )

            executeWithOptimistic({
                fallbackState: '',
                asyncAction: () =>
                    mutateNoteAsync(
                        node.key,
                        updatedNotes,
                        isArticleNoteSpecifier
                    ),
            })

            setContentItemViewNodes(optimisticTreeViewNodes)
        },
        [contentItemViewNodes, executeWithOptimistic, mutateNoteAsync]
    )

    return {
        contentItemViewNodes,
        findContentItemNode,
        insertNodeAfterSibling,
        setAddingNode,
        createContentItemViewNode,
        handleOptimisticAddAction,
        previousContentItemViewNodes,
        nodeToAdd,
        addNewArticle,
        addNewParagraph,
        deleteSpecifierNote,
        updateText,
    }
}
